/**
 * Attorney Coverage — the state -> attorney routing table (slice 1 of attorney question
 * routing). See docs/DESIGN_ATTORNEY_QUESTION_ROUTING.md
 *
 * SHIPS DARK. Nothing routes or emails off this table yet. It exists first so coverage is
 * correct and reviewed before the routing engine dispatches its first member question — a
 * state with no active attorney becomes an exception at 06:00, so the gap is worth finding
 * here rather than in a batch run.
 *
 * Read is admin/superadmin/manager; write is admin/superadmin. The route guard mirrors that,
 * but the RPCs enforce it — a manager who reaches the write controls still gets refused
 * server-side, so the disabled buttons below are an affordance, not the security boundary.
 */

import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import { featureFlags } from "../../utils/featureFlags";
import Button from "../../components/common/Button";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import {
  listAttorneyCoverage,
  listAttorneyCandidates,
  upsertAttorneyCoverage,
  setAttorneyCoverageActive,
  US_STATES,
  type AttorneyCoverageRow,
} from "../../services/attorneyCoverage.service";

const WRITE_ROLES = ["admin", "superadmin"];

interface FormState {
  id: string | null;
  state_code: string;
  attorney_id: string;
  is_backup: boolean;
  effective_from: string;
  effective_to: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  id: null,
  state_code: "",
  attorney_id: "",
  is_backup: false,
  effective_from: "",
  effective_to: "",
  notes: "",
};

/**
 * Dark-flag gate. Returns BEFORE any query is declared, so a flagged-off environment issues
 * no RPCs at all rather than rendering an empty shell that still talks to the database.
 */
function FeatureOffNotice() {
  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
        <strong className="block text-gray-900 mb-1">Not available yet</strong>
        Attorney question routing is still being built and is not switched on in
        this environment.
      </div>
    </div>
  );
}

const AttorneyCoverageInner: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [includeInactive, setIncludeInactive] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  // Matches RequireRole's normalization so "super_admin" and "superadmin" behave alike.
  const canWrite = WRITE_ROLES.includes(
    (user?.role ?? "").toLowerCase().replace(/_/g, ""),
  );

  const coverageQ = useQuery({
    queryKey: ["attorney-coverage", includeInactive],
    queryFn: () => listAttorneyCoverage(includeInactive),
  });
  const candidatesQ = useQuery({
    queryKey: ["attorney-candidates"],
    queryFn: listAttorneyCandidates,
  });

  const rows: AttorneyCoverageRow[] = coverageQ.data ?? [];
  const candidates = candidatesQ.data ?? [];

  // Which states have no active PRIMARY. This is the number that matters: at dispatch time a
  // state with no active primary produces an exception instead of an answer.
  const uncoveredCount = useMemo(() => {
    const covered = new Set(
      rows.filter((r) => r.active && !r.is_backup).map((r) => r.state_code),
    );
    return US_STATES.length - covered.size;
  }, [rows]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["attorney-coverage"] });
  };

  const handleSave = async () => {
    if (!form) {
      return;
    }
    if (!form.state_code || !form.attorney_id) {
      showToast("error", "Choose a state and an attorney.");
      return;
    }
    setSaving(true);
    try {
      await upsertAttorneyCoverage({
        id: form.id,
        state_code: form.state_code,
        attorney_id: form.attorney_id,
        is_backup: form.is_backup,
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
        notes: form.notes || null,
      });
      showToast("success", form.id ? "Coverage updated." : "Coverage added.");
      setForm(null);
      refresh();
    } catch (e) {
      showToast("error", (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (row: AttorneyCoverageRow) => {
    try {
      await setAttorneyCoverageActive(row.id, !row.active);
      showToast(
        "success",
        row.active ? "Coverage deactivated." : "Coverage reactivated.",
      );
      refresh();
    } catch (e) {
      showToast("error", (e as Error).message);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Attorney Coverage
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Which attorney answers member questions from each state.
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => setForm({ ...EMPTY_FORM })} variant="primary">
            Add coverage
          </Button>
        )}
      </div>

      {/* Say plainly that this is not wired up yet, so nobody assumes questions are flowing. */}
      <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <strong>Not live yet.</strong> Nothing is routed or emailed from this
        table today. It is filled in ahead of the routing engine so coverage is
        correct before the first member question is dispatched.
      </div>

      {coverageQ.error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {(coverageQ.error as Error).message}
        </div>
      )}

      {candidatesQ.data && candidates.length === 0 && (
        <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          <strong>No attorney accounts yet.</strong> Coverage can only point at
          a user who has the <em>Attorney</em> role. Create those accounts
          first, then assign states here.
        </div>
      )}

      <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
        <div className="text-sm text-gray-600">
          {rows.filter((r) => r.active && !r.is_backup).length} states with an
          active primary
          {uncoveredCount > 0 && (
            <span className="ml-2 text-yellow-700">
              · {uncoveredCount} state{uncoveredCount === 1 ? "" : "s"} still
              uncovered
            </span>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Show inactive
        </label>
      </div>

      {form && canWrite && (
        <div className="mb-6 rounded-lg border border-gray-300 bg-white p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {form.id ? "Edit coverage" : "Add coverage"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label
                htmlFor="ac-state"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                State
              </label>
              <select
                id="ac-state"
                value={form.state_code}
                onChange={(e) =>
                  setForm({ ...form, state_code: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md text-sm py-2 px-2 bg-white"
              >
                <option value="">Select a state…</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="ac-attorney"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Attorney
              </label>
              <select
                id="ac-attorney"
                value={form.attorney_id}
                onChange={(e) =>
                  setForm({ ...form, attorney_id: e.target.value })
                }
                disabled={candidates.length === 0}
                className="w-full border border-gray-300 rounded-md text-sm py-2 px-2 bg-white disabled:bg-gray-100"
              >
                <option value="">
                  {candidates.length === 0
                    ? "No attorney accounts"
                    : "Select an attorney…"}
                </option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name || c.email || c.id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="ac-role"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Role for this state
              </label>
              <select
                id="ac-role"
                value={form.is_backup ? "backup" : "primary"}
                onChange={(e) =>
                  setForm({ ...form, is_backup: e.target.value === "backup" })
                }
                className="w-full border border-gray-300 rounded-md text-sm py-2 px-2 bg-white"
              >
                <option value="primary">Primary — receives the question</option>
                <option value="backup">
                  Backup — used when no primary is active
                </option>
              </select>
            </div>
            <div>
              <label
                htmlFor="ac-from"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Effective from <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="ac-from"
                type="date"
                value={form.effective_from}
                onChange={(e) =>
                  setForm({ ...form, effective_from: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md text-sm py-2 px-2"
              />
            </div>
            <div>
              <label
                htmlFor="ac-to"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Effective to <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="ac-to"
                type="date"
                value={form.effective_to}
                onChange={(e) =>
                  setForm({ ...form, effective_to: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md text-sm py-2 px-2"
              />
            </div>
            <div>
              <label
                htmlFor="ac-notes"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Notes <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="ac-notes"
                type="text"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full border border-gray-300 rounded-md text-sm py-2 px-2"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={handleSave} variant="primary" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              onClick={() => setForm(null)}
              variant="secondary"
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {coverageQ.isLoading ? (
        <LoadingSpinner />
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
          No attorney coverage configured yet. Every state needs an active
          primary attorney before questions can be routed.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  State
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Attorney
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Role
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Effective
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Status
                </th>
                {canWrite && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className={r.active ? "" : "bg-gray-50 text-gray-500"}
                >
                  <td className="px-4 py-2 font-medium">{r.state_code}</td>
                  <td className="px-4 py-2">
                    <div>{r.attorney_name || "—"}</div>
                    <div className="text-xs text-gray-400">
                      {r.attorney_email}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs ${
                        r.is_backup
                          ? "bg-gray-100 text-gray-700"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {r.is_backup ? "Backup" : "Primary"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {r.effective_from || "—"} → {r.effective_to || "open"}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs ${
                        r.active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {r.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {canWrite && (
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        className="text-blue-600 hover:underline mr-3"
                        onClick={() =>
                          setForm({
                            id: r.id,
                            state_code: r.state_code,
                            attorney_id: r.attorney_id,
                            is_backup: r.is_backup,
                            effective_from: r.effective_from ?? "",
                            effective_to: r.effective_to ?? "",
                            notes: r.notes ?? "",
                          })
                        }
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-gray-600 hover:underline"
                        onClick={() => handleToggleActive(r)}
                      >
                        {r.active ? "Deactivate" : "Reactivate"}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AttorneyCoverage: React.FC = () =>
  featureFlags.attorneyQuestionRouting ? (
    <AttorneyCoverageInner />
  ) : (
    <FeatureOffNotice />
  );

export default AttorneyCoverage;
